import React, { Component, ComponentType } from 'react';
import { Alert } from 'antd';
import { uniq, isEqual } from 'lodash';

const initialState = {
  isAll: false, // 是否为全选
  selectedIds: [] as string[], // 已选内容
  unSelectedIds: [] as string[], // 未选内容
};
const defaultProps = {};

type State = Readonly<typeof initialState>;
type DefaultProps = typeof defaultProps;
type Props = {} & DefaultProps;

export type BatchLogicWrapperProps<T extends Record<string, any>> = {} & State & Props & T;

export default function batchLogic<T extends Record<string, any>>(
  WrapperComponent: ComponentType<BatchLogicWrapperProps<T>>
) {
  return class BatchLogicWrapper extends Component<Props & T, State> {
    static defaultProps = defaultProps;
    readonly state = initialState;

    componentDidUpdate(prevProps: Props & T) {
      const { source } = this.props;

      if (prevProps.source && source) {
        if (
          isEqual(prevProps.source.page, source.page) &&
          !isEqual(prevProps.source.data, source.data)
        ) {
          this.setState({ isAll: false, selectedIds: [], unSelectedIds: [] });
        } else if (!isEqual(prevProps.source.data, source.data)) {
          const { isAll, unSelectedIds } = this.state;
          if (isAll) {
            this.setState({
              selectedIds: source.data
                .map(this.getId)
                .filter((id: string) => !unSelectedIds.some((unId) => unId === id)),
            });
          }
        }
      }
    }

    get total() {
      return (this.props.source.page || {}).total || 0;
    }
    get rowSelection() {
      const { rowSelection } = this.props;
      const { selectedIds } = this.state;

      if (rowSelection === null) {
        return null;
      }
      return {
        columnWidth: 50,
        hideDefaultSelections: true,
        selectedRowKeys: selectedIds,
        onSelect: (record: T, isSelected: boolean) => {
          const { isAll, selectedIds, unSelectedIds } = this.state;

          const id = this.getId(record, 0);
          const ids = !isSelected
            ? selectedIds.filter((v) => v !== id)
            : selectedIds.includes(id)
            ? selectedIds
            : [...selectedIds, id];
          // 当全选状态的时候 且取消的时候，就在
          if (isAll) {
            this.setState({
              unSelectedIds: isSelected
                ? unSelectedIds.filter((v) => v !== id)
                : unSelectedIds.includes(id)
                ? unSelectedIds
                : [...unSelectedIds, id],
              selectedIds: ids,
            });
          } else {
            // 非全选状态
            this.setState({ selectedIds: ids });
          }
        },
        onSelectAll: (isSelected: boolean, selectedRows: any[], changeRows: any[]) => {
          const { isAll, selectedIds, unSelectedIds } = this.state;

          const ids = isSelected
            ? uniq([...selectedIds, ...selectedRows.map(this.getId)])
            : uniq(
                selectedIds.filter((id) => !changeRows.some((item) => this.getId(item, 0) === id))
              );
          if (isAll) {
            this.setState({
              unSelectedIds: isSelected
                ? uniq(
                    unSelectedIds.filter(
                      (id) => !selectedRows.some((item) => this.getId(item, 0) === id)
                    )
                  )
                : uniq(unSelectedIds.concat(changeRows.map(this.getId))),
              selectedIds: ids,
            });
          } else {
            this.setState({ selectedIds: ids });
          }
        },
      };
    }
    get selectedCount() {
      const { isAll, selectedIds, unSelectedIds } = this.state;

      return isAll ? this.total - unSelectedIds.length : selectedIds.length;
    }
    get renderMessage() {
      const { selectedIds, unSelectedIds, isAll } = this.state;

      return (selectedIds && selectedIds.length) || isAll ? (
        <Alert
          showIcon
          type="info"
          message={
            <div>
              已选择 <a>{this.selectedCount}</a> 项 {/* 总计: {this.total} */}
              {this.total === this.selectedCount && !unSelectedIds.length ? null : (
                <a style={{ marginLeft: 15 }} onClick={this.handleSelectAll}>
                  选择全部
                </a>
              )}
              <a style={{ marginLeft: 15 }} onClick={this.handleSelectEmpty}>
                清空
              </a>
            </div>
          }
        />
      ) : null;
    }
    get selectedRecord() {
      const { isAll, unSelectedIds, selectedIds } = this.state;
      return {
        is_all: Number(isAll),
        ids: isAll ? unSelectedIds : selectedIds,
      };
    }
    render() {
      const props = {} as Record<string, any>;

      return (
        <WrapperComponent
          {...this.props}
          {...this.state}
          message={this.renderMessage}
          rowSelection={this.rowSelection}
          selectedCount={this.selectedCount}
          onSelectAll={this.handleSelectAll}
          selectedRecord={this.selectedRecord}
          onSelectEmpty={this.handleSelectEmpty}
          {...props}
        />
      );
    }

    getId = (record: T, index: number) => {
      const {
        tableOption: { rowKey },
      } = this.props;

      return typeof rowKey === 'function' ? rowKey(record, index) : record[rowKey];
    };
    // 清空
    handleSelectEmpty = () => {
      this.setState({
        isAll: false,
        selectedIds: [],
        unSelectedIds: [],
      });
    };
    // 全选
    handleSelectAll = () => {
      const {
        source: { data },
      } = this.props;
      this.setState({
        isAll: true,
        unSelectedIds: [],
        selectedIds: data.map(this.getId),
      });
    };
  };
}
